import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./AnimatedSection";

export const ServiceCard = ({ service, i }) => {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 200, damping: 20, mass: 0.4 });
  const sry = useSpring(ry, { stiffness: 200, damping: 20, mass: 0.4 });

  const onMove = (e) => {
    if (!ref.current || window.matchMedia("(pointer: coarse)").matches) return;
    const r = ref.current.getBoundingClientRect();
    const dx = (e.clientX - r.left) / r.width - 0.5;
    const dy = (e.clientY - r.top) / r.height - 0.5;
    rx.set(-dy * 5);
    ry.set(dx * 5);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  const Icon = service.icon;
  const shown = service.capabilities.slice(0, 5);
  const extra = service.capabilities.length - shown.length;

  return (
    <Reveal delay={i * 0.1} className="h-full [perspective:1200px]">
      <div className="group h-full rounded-3xl bg-gradient-to-br from-[#3CBE6E]/0 via-[#1C7543]/0 to-[#B8912A]/0 p-[1.5px] transition-all duration-500 hover:from-[#3CBE6E]/70 hover:via-[#1C7543]/70 hover:to-[#B8912A]/70">
        <motion.div
          ref={ref}
          onMouseMove={onMove}
          onMouseLeave={onLeave}
          style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
          data-testid={`service-card-${service.id}`}
          className="flex h-full flex-col rounded-3xl border border-[#07130C]/10 bg-white p-8 transition-shadow duration-500 group-hover:shadow-[0_28px_70px_rgba(14,58,40,0.16)]"
        >
          <div className="flex items-start justify-between">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1C7543] to-[#D3B74D] text-white shadow-[0_10px_24px_rgba(28,117,67,0.35)] transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-110">
              <Icon className="h-5 w-5" />
            </span>
            <span className="font-display text-sm font-semibold text-[#07130C]/25">
              {service.index}
            </span>
          </div>
          <h3 className="mt-6 font-display text-xl font-semibold tracking-tight text-[#07130C] md:text-2xl">
            {service.title}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-[#07130C]/60">{service.copy}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {shown.map((c) => (
              <span
                key={c}
                className="rounded-full border border-[#07130C]/10 bg-[#F8FBF6] px-3 py-1 text-[11px] font-medium text-[#07130C]/70"
              >
                {c}
              </span>
            ))}
            {extra > 0 && (
              <span className="rounded-full border border-[#1C7543]/25 bg-[#1C7543]/5 px-3 py-1 text-[11px] font-semibold text-[#1C7543]">
                +{extra} more
              </span>
            )}
          </div>
          <Link
            to={`/services#${service.id}`}
            data-testid={`service-card-link-${service.id}`}
            className="mt-auto inline-flex items-center gap-1.5 pt-6 text-sm font-semibold text-[#1C7543] transition-colors duration-200 hover:text-[#D3B74D]"
          >
            Explore capability
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </motion.div>
      </div>
    </Reveal>
  );
};
